import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getChannels } from "../../api/ltv/ltv";
import { SessionContext } from "../../context/SessionContext";
import { getCinemaCopy } from "../../statics/cinemaCopy";
import useCarouselScroll from "../../utils/useCarouselScroll";
import CinemaIcon from "./CinemaIcon";
import ChannelLogo from "./ChannelLogo";
import NowOnAirShelf from "./NowOnAirShelf";
import "./LiveChannelShelf.scss";

export default function LiveChannelShelf() {
  const copy = getCinemaCopy();
  const { profile } = useContext(SessionContext);
  const profileId = profile?.id;
  const [channels, setChannels] = useState([]);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const [now, setNow] = useState(Date.now);
  const { trackRef, canPrevious, canNext, move } = useCarouselScroll({
    itemCount: channels.length,
    resetKey: profileId
  });

  useEffect(() => {
    let active = true;
    setChannels([]);
    setStatus("loading");
    let pending = false;
    const refresh = () => {
      if (document.hidden || pending) return;
      setNow(Date.now());
      pending = true;
      // Both shelves share one profile-scoped list, including its EPGs.
      getChannels()
        .then(({ data }) => {
          if (!Array.isArray(data)) throw new Error("Invalid channel list");
          if (!active) return;
          setChannels(data.filter((channel) => channel?.id));
          setNow(Date.now());
          setStatus("ready");
        })
        .catch(() => {
          if (active)
            setStatus((current) => (current === "ready" ? current : "error"));
        })
        .finally(() => {
          pending = false;
        });
    };
    refresh();
    const timer = setInterval(refresh, 30000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [profileId, attempt]);

  if (status === "ready" && !channels.length) return null;

  return (
    <>
      <section
        className="cinema-shelf cinema-live-channels"
        aria-label={copy.live}
        aria-busy={status === "loading"}
      >
        <div className="cinema-shelf-heading">
          <h2 className="label-title">{copy.live}</h2>
          {status === "ready" && (
            <div className="cinema-shelf-controls">
              <button
                type="button"
                className="cinema-icon-button"
                aria-label={`${copy.previous}: ${copy.live}`}
                disabled={!canPrevious}
                onClick={() => move(-1)}
              >
                <CinemaIcon name="previous" />
              </button>
              <button
                type="button"
                className="cinema-icon-button"
                aria-label={`${copy.next}: ${copy.live}`}
                disabled={!canNext}
                onClick={() => move(1)}
              >
                <CinemaIcon name="next" />
              </button>
            </div>
          )}
        </div>
        {status === "error" ? (
          <div className="cinema-channel-error">
            <p role="alert">{copy.loadError}</p>
            <button
              type="button"
              className="cinema-button cinema-button-secondary"
              onClick={() => setAttempt((value) => value + 1)}
            >
              {copy.retry}
            </button>
          </div>
        ) : (
          <>
            {status === "loading" && (
              <span className="atlas-status-sr" role="status">
                {copy.loading}
              </span>
            )}
            <div className="cinema-channel-track" ref={trackRef}>
              {status === "loading"
                ? Array.from({ length: 10 }, (_, index) => (
                    <div
                      className="cinema-channel-skeleton"
                      key={index}
                      aria-hidden="true"
                    />
                  ))
                : channels.map((channel) => (
                    <Link
                      key={channel.id}
                      className="cinema-channel-card"
                      to={`/dashboard/livetv/${channel.id}`}
                      title={channel.name}
                    >
                      <ChannelLogo channel={channel} />
                      <span className="cinema-channel-name">
                        {channel.name}
                      </span>
                    </Link>
                  ))}
            </div>
          </>
        )}
      </section>
      {status === "ready" && (
        <NowOnAirShelf channels={channels} now={now} profileId={profileId} />
      )}
    </>
  );
}
